/**
 * SII leadership — the only approved, published people photography available.
 * Rendered in this order, from Appendix A.1. Alt text is real and descriptive.
 *
 * The Board of Directors, Executive Committee and Group Leadership grids are
 * deliberately seeded EMPTY and render <PendingContent>: SII leadership and
 * Group leadership are different bodies, and conflating them would be a
 * governance error.
 *
 * Individual staff email addresses are never published on the corporate site.
 */
export const SII_LEADERSHIP = [
  { key: 'people/melvin-tan', name: 'Melvin Tan', role: 'Chief Executive Officer' },
  { key: 'people/sue-lim', name: 'Sue Lim', role: 'Chief Operating Officer' },
  { key: 'people/louis-leow', name: 'Louis Leow', role: 'Executive Director & Chief Financial Officer' },
  { key: 'people/paul-leong', name: 'Paul Leong', role: 'Head of Corporate Development' },
  { key: 'people/emily-ting', name: 'Emily Ting', role: 'Finance & Human Resource Manager' },
  { key: 'people/joey-chua', name: 'Joey Chua', role: 'Market Development Manager' },
  { key: 'people/tan-hwee-lang', name: 'Tan Hwee Lang', role: 'Lab Manager' },
  { key: 'people/steven-lee', name: 'Steven Lee', role: 'Assistant Service & Technical Manager' },
  { key: 'people/kalaiarasan', name: 'Kalaiarasan', role: 'Assistant Service & Technical Manager' },
];

export const CERTIFICATIONS = [
  { key: 'certs/bizsafe-level-3', name: 'bizSAFE Level 3', alt: 'bizSAFE Level 3 certification badge awarded to SII.' },
  { key: 'certs/iso-9001-2015', name: 'ISO 9001:2015', alt: 'ISO 9001:2015 quality management certification badge awarded to SII.' },
  {
    key: 'certs/iso-17025',
    name: 'ISO 17025 · LA-2017-0658-C',
    alt: 'ISO 17025 laboratory accreditation badge, accreditation number LA-2017-0658-C, awarded to SII.',
  },
];

/** Marine brand-partner logos, as published by the brand owners. */
export const MARINE_BRANDS = [
  { key: 'brands/hidea', name: 'HIDEA' },
  { key: 'brands/tohatsu', name: 'Tohatsu', maxHeight: 58 },
  { key: 'brands/honda-marine', name: 'Honda Marine', maxHeight: 40 },
  { key: 'brands/multiflex', name: 'Multiflex' },
  { key: 'brands/seafirst', name: 'Seafirst Engineering' },
];

/** Brands with no wordmark file we are licensed to publish, set in type instead. */
export const TYPE_ONLY_BRANDS = [];

/**
 * Outboard-motor ranges Supratechnic carries, by brand.
 *
 * Series and model designations are taken from the manufacturers' own published
 * ranges — hideapower.com and tohatsu.com — and nothing is inferred. Product
 * photography is pending brand-owner consent, so these render as specification
 * cards rather than invented imagery.
 */
export const OUTBOARD_RANGES = [
  {
    brand: 'HIDEA',
    note: 'Hangzhou Hidea Power Machinery. Distributed by Supratechnic across the region.',
    ranges: [
      { name: 'Four-stroke outboards', models: 'F2.5 · F5 · F6 · F9.9 · BF15 · BF20' },
      { name: 'Commercial-duty outboards', models: '25F · 30F · 40F · G40F · J40F · 60F · 75F · EF60 · EF115' },
      { name: 'Jet-pump outboards', models: 'HDJD30F · HDJD40F' },
      { name: 'EFI and two-stroke ranges', models: 'Electronic fuel injection and two-stroke series' },
    ],
  },
  {
    brand: 'Tohatsu',
    note:
      'Supratechnic (M) Sdn. Bhd. was appointed an authorised dealer of Tohatsu outboard motors by Tai Hua Agencies (K.L) Sdn. Bhd., the official Malaysian distributor, for a sales territory covering Peninsular Malaysia (SGXNet announcement, 18 August 2026).',
    ranges: [
      { name: 'Portable four-strokes (2.5–20 hp)', models: 'MFS2.5C · MFS3.5C · MFS4D · MFS5D · MFS6D · MFS8C · MFS9.9E · MFS15E · MFS20E' },
      { name: 'Mid-range four-strokes (25–140 hp)', models: 'MFS25D · MFS30D · MFS40A · MFS50A · MFS60A · MFS75A · MFS90A · MFS100A · MFS115A · MFS140A' },
      { name: 'High-power outboards (150–250 hp)', models: 'MFS150A · BFT150DK1 · BFT200DK1 · BFT225DK1 · BFT250DK1' },
      { name: 'Two-stroke and EverRun', models: 'M series · MX EverRun series · M25H JET' },
    ],
  },
];

export const MARINE_PRODUCTS = [
  {
    key: 'products/raptor-s-tec',
    name: 'Raptor S-Tec',
    alt: 'The Raptor S-Tec sit-on-top fishing kayak in red, white and black, with a raised seat, paddle and rod holder.',
  },
  {
    key: 'products/raptor-vtec',
    name: 'Raptor VTec',
    alt: 'The Raptor VTec pedal-drive fishing kayak in orange and grey, with a frame seat, pedal unit and paddle.',
  },
];

/**
 * De-listed 22 Jun 2026 on instruction — only the kayaks are available now. The
 * four AB Inflatables records and image files are deliberately LEFT IN PLACE
 * (`products/ab-nautilus-13-dlx`, `ab-oceanus-12-vst`, `ab-navigo-12-vs`,
 * `ab-ventus`) so they can be put back by adding them to the array above. Do not
 * delete the assets: they were deleted once already and had to be recovered from
 * git history.
 */

/** Screens from the Group's digital warranty platform, built by SuprAI. */
export const WARRANTY_SCREENS = [
  {
    key: 'warranty/app-home',
    title: 'One app, four roles',
    caption:
      'The home screen. What a user can open depends on the role they signed in with — branch, HQ, supplier or customer.',
    alt: 'The warranty platform home screen in its HIDEA deployment, showing role-based buttons for the engine registration dashboard, warranty claims, PDI checklist and HQ verification.',
  },
  {
    key: 'warranty/digital-pdi-checklist',
    title: 'Digital pre-delivery inspection',
    caption:
      'The pre-delivery inspection is completed in the app, question by question, with remarks — no paper, and no warranty activated without it.',
    alt: 'The digital pre-delivery inspection checklist in the warranty platform, with a remarks box against each question.',
  },
  {
    key: 'warranty/hidea-final-review',
    title: 'The supplier decides in the same system',
    caption:
      'The supplier — HIDEA in this deployment — sees only its own final-review queue: the case reference, its type, the branch and its status. Approve, reject, request more information, or hold for parts.',
    alt: 'The supplier final-review queue, showing a warranty registration case with its reference, source reference, branch and status.',
  },
];

export const IQAIR_PRODUCTS = [
  { key: 'products/iqair-atem-x', name: 'IQAir Atem X', alt: 'IQAir Atem X air purifier, distributed in Singapore by SupraYi.' },
  { key: 'products/iqair-healthpro-250', name: 'IQAir HealthPro 250', alt: 'IQAir HealthPro 250 air purifier, distributed in Singapore by SupraYi.' },
  { key: 'products/iqair-healthpro-150', name: 'IQAir HealthPro 150', alt: 'IQAir HealthPro 150 air purifier, distributed in Singapore by SupraYi.' },
  { key: 'products/iqair-healthpro-100', name: 'IQAir HealthPro 100', alt: 'IQAir HealthPro 100 air purifier, distributed in Singapore by SupraYi.' },
  { key: 'products/iqair-gc-multigas', name: 'IQAir GC MultiGas', alt: 'IQAir GC MultiGas air purifier for gas and chemical filtration.' },
  { key: 'products/iqair-airvisual-pro', name: 'IQAir AirVisual Pro', alt: 'IQAir AirVisual Pro air-quality monitor.' },
];

export const SII_PRODUCTS = [
  { key: 'products/igd-mk8-gas-detector', name: 'IGD MK8 flammable gas detector', alt: 'IGD MK8 pellistor flammable gas detector supplied by SII.' },
  { key: 'products/igd-voc-detector', name: 'IGD VOC detector', alt: 'IGD volatile organic compound detector supplied by SII.' },
  { key: 'products/igd-control-panels', name: 'IGD control panels & sensor heads', alt: 'IGD addressable gas-detection control panels and sensor heads supplied by SII.' },
  { key: 'products/logico2-mk9', name: 'LogiCO2 Mk9 detector set', alt: 'LogiCO2 Mk9 carbon dioxide detector set supplied by SII.' },
  { key: 'products/logico2-alert-mk10', name: 'LogiCO2 Alert Mk10 detector set', alt: 'LogiCO2 Alert Mk10 carbon dioxide detector set supplied by SII.' },
  { key: 'products/logico2-scout-glow', name: 'LogiCO2 Scout Glow', alt: 'LogiCO2 Scout Glow portable carbon dioxide alarm supplied by SII.' },
  { key: 'products/ellab-thermal-validation', name: 'Ellab thermal validation', alt: 'Ellab thermal validation equipment supplied by SII.' },
  { key: 'products/ellab-sensor-array', name: 'Ellab sensor array', alt: 'Ellab wireless sensor array for thermal validation, supplied by SII.' },
];

export const INDUSTRIES = [
  { key: 'industries/pharmaceutical', name: 'Pharmaceutical', alt: 'Pharmaceutical manufacturing environment served by SII instrumentation.' },
  { key: 'industries/semiconductor-electronics', name: 'Semiconductor & electronics', alt: 'Semiconductor and electronics fabrication environment served by SII instrumentation.' },
  { key: 'industries/beverage-hospitality', name: 'Beverage & hospitality', alt: 'Beverage and hospitality environment served by SII carbon dioxide safety systems.' },
  { key: 'industries/healthcare', name: 'Healthcare', alt: 'Healthcare environment served by SII instrumentation and air-quality solutions.' },
  { key: 'industries/education', name: 'Education', alt: 'Education and research laboratory served by SII instrumentation.' },
];

export const SUPRAYI_CLIENTS = [
  { key: 'clients/nus', name: 'National University of Singapore' },
  { key: 'clients/singhealth', name: 'SingHealth' },
  { key: 'clients/huawei', name: 'Huawei' },
  { key: 'clients/gleneagles', name: 'Gleneagles' },
  { key: 'clients/aia', name: 'AIA' },
  { key: 'clients/airbnb', name: 'Airbnb' },
  { key: 'clients/novena-cancer-centre', name: 'Novena Cancer Centre' },
  { key: 'clients/carlton-hotel', name: 'Carlton Hotel' },
  { key: 'clients/the-little-gym', name: 'The Little Gym' },
];

/** Frac people — hidden /frac route ONLY. Never rendered on a public route. */
export const FRAC_PEOPLE = [
  { key: 'frac/japhet-lim', name: 'Japhet Lim', role: 'Co-Founder & Chief Executive Officer / Chief Transformation Officer' },
  { key: 'frac/melvin-tan', name: 'Melvin Tan', role: 'Co-Founder & Chairperson' },
  { key: 'frac/philip-tse', name: 'Philip Tse', role: 'Chief Financial Officer' },
  { key: 'frac/jeanne-lim', name: 'Jeanne Lim', role: 'Public Relations Lead' },
  { key: 'frac/angela-tay', name: 'Angela Tay', role: 'Community Lead' },
  { key: 'frac/kennick-kek', name: 'Kennick Kek', role: 'Innovation & Research Lead' },
  { key: 'frac/jeffri-said', name: 'Jeffri Said', role: 'Islamic Finance Lead' },
  { key: 'frac/paul-leong', name: 'Paul Leong', role: 'Head of Corporate Development' },
];
